import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { Award, Clock, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestInterface() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTest();
  }, [testId]);

  useEffect(() => {
    if (test && !submitted) {
      setTimeRemaining(test.duration_minutes * 60);
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [test, submitted]);

  const fetchTest = async () => {
    try {
      const response = await axios.get(`${API}/tests?test_id=${testId}`, getAuthHeader());
      const tests = response.data;
      if (tests.length > 0) {
        setTest(tests[0]);
      }
    } catch (error) {
      toast.error('Failed to load test');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(
        `${API}/tests/submit`,
        { test_id: testId, answers },
        getAuthHeader()
      );
      setResult(response.data);
      setSubmitted(true);
      toast.success('Test submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit test');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Test not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50 flex items-center justify-center px-4">
        <Card data-testid="test-result" className="bg-white rounded-xl shadow-lg shadow-indigo-500/10 p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-teal-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Test Submitted!</h2>
          <div className="bg-slate-50 rounded-lg p-6 mb-6">
            <p className="text-sm text-slate-600 mb-2">Your Score</p>
            <p className="text-4xl font-bold text-indigo-600">
              {result?.score || 0} / {result?.total_marks || 0}
            </p>
          </div>
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/student/dashboard')}
            className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900">{test.title}</h1>
                <p className="text-xs text-slate-600">Total Marks: {test.total_marks}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-amber-100 px-4 py-2 rounded-lg">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{formatTime(timeRemaining)}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card data-testid="test-questions" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 mb-6">
          <div className="space-y-8">
            {test.questions.map((question, index) => (
              <div key={question.id} data-testid={`question-${index}`} className="border-b border-slate-200 pb-6 last:border-b-0">
                <div className="flex items-start mb-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-semibold text-sm mr-3">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-lg font-medium text-slate-900 mb-4">{question.question}</p>
                    <RadioGroup
                      value={answers[question.id]}
                      onValueChange={(value) => setAnswers({ ...answers, [question.id]: value })}
                    >
                      {question.options?.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center space-x-3 mb-3">
                          <RadioGroupItem
                            value={option}
                            id={`q${question.id}-opt${optIndex}`}
                            data-testid={`question-${index}-option-${optIndex}`}
                          />
                          <Label
                            htmlFor={`q${question.id}-opt${optIndex}`}
                            className="text-slate-700 cursor-pointer flex-1 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            {option}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button
            data-testid="submit-test-btn"
            onClick={handleSubmit}
            className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base font-semibold shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 transition-all duration-200"
          >
            Submit Test
          </Button>
        </div>
      </main>
    </div>
  );
}
